// noinspection SpellCheckingInspection

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { addSpacesToTitleCase } from "_shared/_utilities/add-spaces-to-title-case";
import { enumStringKeys } from "_shared/_utilities/enum-utils";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import {
  getDocumentsByQuery,
  getDocumentsQueryByInterface,
  getIsLoadingDocumentsByQuery,
  populateDocumentsByQuery,
  updateDocumentsInterfaceQuery,
} from "documents/_stores/documents-redux-slice";
import Document from "documents/document";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import {
  getEntityAgreementsByIds,
  getIsLoadingEntityAgreementByIds,
  populateEntityAgreements,
} from "legal-entities/agreements/_stores/entity-agreements-redux-slice";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import {
  getForumQueryByInterface,
  getForumsByQuery,
  getIsLoadingForumsByQuery,
  populateForumsByQuery,
  updateForumInterfaceQuery,
} from "messaging/forums/_stores/forums-redux-slice";
import Forum from "messaging/forums/forum";
import { ChildResourceType } from "permissions/resources/types";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import { ProposalBoxType, ProposalContext, ProposalOrderField } from "work/proposals/__constants/constants";
import { useGetProposalsQuery } from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import {
  getProposalQueryByInterface,
  isProposalLoadIssue,
  updateProposalInterfaceQuery,
} from "work/proposals/_store/proposals-redux-slice";
import Proposal from "work/proposals/proposal";
import { WorkAgreementOrderField } from "work/work-agreements/_api/queries/_fields/work-agreement-order-field";
import WorkAgreementQuery, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import { useGetWorkAgreementsQuery } from "work/work-agreements/_api/work-agreement-api";
import {
  getWorkAgreementQueryByInterface,
  isWorkAgreementLoadIssue,
  updateWorkAgreementInterfaceQuery,
} from "work/work-agreements/_store/work-agreement-redux-slice";
import WorkAgreement from "work/work-agreements/work-agreement";

// Styles
const SplitSearchContainer = styled("div")(() => ({
  width: "100%",
}));
const ControlContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
}));
const TargetFormControl = styled(FormControl)(() => ({
  flex: 1,
  width: "100%",
}));
const CategoryDropdown = styled(Button)(({ theme }) => ({
  borderBottomRightRadius: 0,
  borderTopRightRadius: 0,
  fontSize: "0.8em",
  padding: `0 ${theme.spacing(2.5)}`,
}));
const SearchTextField = styled(TextField)(({ theme }) => ({
  boxSizing: "border-box",
  minWidth: "275px",
  width: "100%",
  "& .MuiInputBase-root": {
    borderRadius: "0 4px 4px 0",
    "& fieldset": {
      borderColor: "#777",
      borderLeftWidth: 0,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
}));

// Types
export type Kind = Extract<keyof typeof ChildResourceType, string>;
export type SearchOption = {
  kind: Kind;
  id: string;
  label: string;
  secondary?: string;
};
type SplitSearchProps = {
  className?: string;
  disabled?: boolean;
  onTargetSelected: (target: SearchOption | null) => void;
};

export default function SplitSearch(props: Readonly<SplitSearchProps>) {
  // Props
  const { className, disabled, onTargetSelected } = props;

  // Refs
  const menuAnchorRef = React.useRef<HTMLButtonElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    setMenuAnchorEl(menuAnchorRef.current);
  }, []);

  // Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  // Helper to convert Kind to a human-readable label
  // e.g. ProposalAccess -> Proposal
  // e.g. WorkAgreementAccess -> Work Agreement
  const kindToLabel = (k: Kind) => addSpacesToTitleCase(k.replace("Access", ""));

  // Memoized Categories
  const categories = React.useMemo<Kind[]>(() => enumStringKeys(ChildResourceType), []);

  // Documents Query
  const documentsInterfaceName = "10RecentContracts";
  const documentQuery = useSelector(getDocumentsQueryByInterface(documentsInterfaceName));
  const documents = useSelector(getDocumentsByQuery(documentQuery));
  const documentsLoading = useSelector(getIsLoadingDocumentsByQuery(documentQuery));

  // Proposals Query
  const proposalsInterfaceName = "10RecentProposals";
  const proposalQueryParams = useSelector(getProposalQueryByInterface(proposalsInterfaceName));
  const proposalQuery = useGetProposalsQuery(
    { interfaceName: proposalsInterfaceName, query: proposalQueryParams! }, 
    { skip: !proposalQueryParams }
  );
  const loadedProposals: Proposal[] = (() => {
    if (!proposalQuery?.data) return [];
    const paginatedProposals = proposalQuery.data;
    const proposals: Proposal[] = [];
    for (const proposalOrIssue of paginatedProposals?.data ?? []) {
      if (isProposalLoadIssue(proposalOrIssue)) continue;
      const proposal = Proposal.fromJSON({ currentUser: session.user!, json: proposalOrIssue });
      if (!proposal) continue;
      proposals.push(proposal);
    }
    return proposals;
  })();
  const proposalsLoading = proposalQuery.isLoading;
  
  // Work Agreements Query
  const workAgreementsInterfaceName = "10RecentWorkAgreements";
  const workAgreementQueryParams = useSelector(getWorkAgreementQueryByInterface(workAgreementsInterfaceName));
    const workAgreementQuery = useGetWorkAgreementsQuery(
    { interfaceName: workAgreementsInterfaceName, query: workAgreementQueryParams! }, 
    { skip: !workAgreementQueryParams }
  );
  const loadedWorkAgreements: WorkAgreement[] = (() => {
    if (!workAgreementQuery?.data) return [];
    const paginatedWorkAgreements = workAgreementQuery.data;
    const workAgreements: WorkAgreement[] = [];
    for (const workAgreementOrIssue of paginatedWorkAgreements?.data ?? []) {
      if (isWorkAgreementLoadIssue(workAgreementOrIssue)) continue;
      const workAgreement = WorkAgreement.fromJSON(workAgreementOrIssue);
      if (!workAgreement) continue;
      workAgreements.push(workAgreement);
    }
    return workAgreements;
  })();
  const workAgreementsLoading = workAgreementQuery.isLoading;

  // Forums Query
  const forumsInterfaceName = "10RecentForums";
  const forumQuery = useSelector(getForumQueryByInterface(forumsInterfaceName));
  const forums = useSelector(getForumsByQuery(forumQuery));
  const forumsLoading = useSelector(getIsLoadingForumsByQuery(forumQuery));

  // Entity Agreements Query
  const entityAgreementIds = session.openEntityAgreements.map((entityAgreement) => entityAgreement.id);
  const entityAgreements = useSelector(getEntityAgreementsByIds(entityAgreementIds));
  const entityAgreementsLoading = useSelector(getIsLoadingEntityAgreementByIds(entityAgreementIds));

  // Loading State
  const isLoading: boolean =
    documentsLoading || proposalsLoading || workAgreementsLoading || forumsLoading || entityAgreementsLoading;

  // State
  const [selectedCategory, setSelectedCategory] = React.useState<Kind | null>(null);
  const [categoryMenuOpen, setCategoryMenuOpen] = React.useState<boolean>(false);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [target, setTarget] = React.useState<SearchOption | null>(null);
  
  // Debounced Search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(searchTerm);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // Lifecycle
  useEffect(() => {
    if (selectedCategory !== "EntityAgreementAccess") return;
    if (entityAgreements === undefined && !entityAgreementsLoading) {
      dispatch(
        populateEntityAgreements({
          session: session,
          entityAgreementIds: entityAgreementIds,
        })
      );
    } 
  }, [selectedCategory, entityAgreements, entityAgreementIds, entityAgreementsLoading, dispatch, session]);

  // Switch between "recent 10" and "search mode"
  useEffect(() => {
    if (!selectedCategory) return;

    const isSearching = debouncedSearchTerm.length > 0;

    switch (selectedCategory) {
      case "DocumentAccess": {
        if (selectedCategory !== "DocumentAccess") break;
        const query = new DocumentQuery({
          filterParameters: new DocumentFilterParameters({
            query: isSearching ? debouncedSearchTerm : undefined,
          }),
          orderParameters: new DocumentOrderParameters({
            orderBy: DocumentOrderField.Created,
            orderDir: OrderDirection.Descending,
          }),
          paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 10 }),
        });

        if (!documentQuery) {
          dispatch(
            updateDocumentsInterfaceQuery({
              interfaceName: isSearching ? "SearchDocuments" : "10RecentContracts",
              query,
            })
          );
          break;
        }
        if (documentQuery && documents === undefined && !documentsLoading) {
          dispatch(populateDocumentsByQuery({ session, query }));
        }
        break;
      }
      case "ProposalAccess": {
        if (!session.user?.legalEntityId || selectedCategory !== "ProposalAccess") break;
        const query = new ProposalQuery({
          filterParameters: new ProposalFilterParameters({
            context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
            boxType: ProposalBoxType.AllActive,
          }),
          orderParameters: new ProposalOrderParameters({
            orderBy: ProposalOrderField.Created,
            orderDir: OrderDirection.Descending,
          }),
          paginationParameters: PaginationParameters.create({
            pageIndex: 0,
            pageSize: 10,
          }),
        });
        if (!proposalQueryParams) {
          dispatch(
            updateProposalInterfaceQuery({
              interfaceName: isSearching ? "SearchProposals" : "10RecentProposals",
              query,
            })
          );
        }
        break;
      }
      case "WorkAgreementAccess": {
        if (!session.user?.legalEntityId || selectedCategory !== "WorkAgreementAccess") break;
        const query = WorkAgreementQuery.create({
          filterParameters: WorkAgreementFilterParameters.create({}),
          orderParameters: WorkAgreementOrderParameters.create({
            orderBy: WorkAgreementOrderField.Start,
            orderDirection: OrderDirection.Descending,
          }),
          paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 10 }),
        });

        if (!workAgreementQueryParams) {
          dispatch(
            updateWorkAgreementInterfaceQuery({
              interfaceName: isSearching ? "SearchWorkAgreements" : "10RecentWorkAgreements",
              query,
            })
          );
        }
        break;
      }

      case "ForumAccess": {
        if (selectedCategory !== "ForumAccess") return;
        const query = new ForumQuery({
          filterParameters: new ForumFilterParameters({
            query: isSearching ? debouncedSearchTerm : undefined,
          }),
          orderParameters: new ForumOrderParameters({
            orderBy: ForumOrderField.CreatedDate,
            orderDir: OrderDirection.Descending,
          }),
          paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 10 }),
        });

        if (!forumQuery) {
          dispatch(
            updateForumInterfaceQuery({
              interfaceName: isSearching ? "SearchForums" : "10RecentForums",
              query,
            })
          );
          break;
        }

        if (forumQuery && forums === undefined && !forumsLoading) {
          dispatch(populateForumsByQuery({ session, query }));
        }
        break;
      }
    }
  }, [
    selectedCategory, 
    documents, 
    documentQuery, 
    documentsLoading, 
    dispatch, 
    session, 
    debouncedSearchTerm, 
    proposalQueryParams, 
    workAgreementQuery, 
    workAgreementsLoading, 
    forumQuery, 
    forums, 
    forumsLoading, 
    workAgreementQueryParams
  ]);

  // Option Converters
  // Convert each entity type to a SearchOption to be used in the Autocomplete component
  const toDocumentOption = (d: Document | UnavailableDocument): SearchOption => ({
    kind: "DocumentAccess",
    id: String(d.id),
    label: "name" in d && d.name ? d.name.toString() : "Unavailable Document",
  });

  const toProposalOption = (p: Proposal): SearchOption => ({
    kind: "ProposalAccess",
    id: String(p.id),
    label: p.name?.value ?? `Proposal ${p.id}`,
  });

  const toWorkAgreementOption = (w: WorkAgreement): SearchOption => ({
    kind: "WorkAgreementAccess",
    id: String(w.id),
    label: w.name?.value ?? `Work Agreement ${w.id}`,
  });

  const toForumOption = (f: Forum): SearchOption => ({
    kind: "ForumAccess",
    id: String(f.id),
    label: f.name.toString() ?? `Forum ${f.id}`,
  });

  const toEntityAgreementOption = (e: EntityAgreement): SearchOption => ({
    kind: "EntityAgreementAccess",
    id: String(e.id),
    label: e.entityName.value ?? `Entity Agreement ${e.id}`,
  });

  // All possible options
  const allOptions: SearchOption[] = React.useMemo(
    () => [
      ...(documents?.data ?? []).map(toDocumentOption),
      ...(loadedProposals ?? []).map(toProposalOption),
      ...(forums ?? []).map(toForumOption),
      ...(loadedWorkAgreements ?? []).map(toWorkAgreementOption),
      ...(entityAgreements ?? []).map(toEntityAgreementOption),
    ],
    [documents, loadedProposals, forums, loadedWorkAgreements, entityAgreements]
  );

  // Filtered Options based on selected category
  const filteredOptions = React.useMemo(
    () => (selectedCategory ? allOptions.filter((o) => o.kind === selectedCategory) : []),
    [allOptions, selectedCategory]
  );

  // Handlers

  /**
   * Handle category selection from the dropdown menu
   * @param _event The mouse event
   * @param category The selected category
   */
  function handleCategorySelected(_event: React.MouseEvent, category: Kind) {
    setSelectedCategory(category);
    setCategoryMenuOpen(false);
    setTarget(null);
    setSearchTerm("");
  }

  return (
    <SplitSearchContainer className={className}>
      <TargetFormControl>
        <FormControlLabel
          control={
            <ControlContainer>
              <CategoryDropdown
                ref={menuAnchorRef}
                disabled={disabled || isLoading}
                disableElevation
                variant="contained"
                endIcon={<ArrowDropDownIcon />}
                onClick={() => setCategoryMenuOpen(!categoryMenuOpen)}
              >
                {selectedCategory ? kindToLabel(selectedCategory) : "Select"}
              </CategoryDropdown>
              <Menu
                keepMounted
                open={categoryMenuOpen}
                onClose={() => setCategoryMenuOpen(false)}
                anchorEl={menuAnchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {categories.map((category) => (
                  <MenuItem key={category} onClick={(event) => handleCategorySelected(event, category)}>
                    {kindToLabel(category)}
                  </MenuItem>
                ))}
              </Menu>

              <Autocomplete<SearchOption, false, false, false>
                sx={{ width: "100%" }}
                options={filteredOptions}
                loading={isLoading}
                loadingText="Loading..."
                disabled={disabled || !selectedCategory || isLoading}
                isOptionEqualToValue={(a, b) => a.id === b.id && a.kind === b.kind}
                getOptionLabel={(opt) => opt.label}
                filterOptions={(opts) => opts}
                inputValue={searchTerm}
                onInputChange={(_e, newInput) => setSearchTerm(newInput)}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.kind}:${option.id}`}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>{option.label}</span>
                      {option.secondary && <small style={{ opacity: 0.7 }}>{option.secondary}</small>}
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <SearchTextField
                    {...params}
                    placeholder={
                      selectedCategory ? `Search ${kindToLabel(selectedCategory)}` : "Select category to begin"
                    }
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                            {params.InputProps?.startAdornment}
                          </>
                        ),
                      },
                      htmlInput: { ...params.inputProps },
                    }}
                  />
                )}
                slotProps={{
                  popper: { sx: { zIndex: (t) => t.zIndex.modal + 1 } },
                }}
                value={
                  target ? filteredOptions.find((o) => o.id === target.id && o.kind === target.kind) ?? null : null
                }
                onChange={(_event, value) => {
                  setTarget(value);
                  onTargetSelected(value);
                }}
              />
            </ControlContainer>
          }
          label="Target"
          labelPlacement="top"
          sx={{
            alignItems: "stretch",
            margin: 0,
          }}
          slotProps={{
            typography: {
              fontWeight: "500",
              marginBottom: 1,
              marginRight: "auto",
            },
          }}
        />
      </TargetFormControl>
    </SplitSearchContainer>
  );
}
